import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Wind, TrendingUp, Shield, Cpu } from 'lucide-react';

const Home = () => {
  const navigate = useNavigate();

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <video className="hero-video" autoPlay loop muted playsInline>
          <source src="https://customer-assets.emergentagent.com/job_7e6c4a21-c0d0-4848-af1c-77af12b51f65/artifacts/sfis3c15_20230623T205527532.att.9A33EECE-AB97-4452-9987-0BB9E1D2051B_0.mov" type="video/quicktime" />
        </video>
        
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              Autonomous Wind Infrastructure Platform
            </h1>
            <p className="hero-subtitle">
              Profitable Distributed Energy at Global Scale
            </p>
            <p className="hero-description">
              Revolutionary single-blade carbon fibre VAWT with cloud-optimised algorithms. 
              Delivering 40% efficiency and 4-6 year payback for distributed renewable energy.
            </p>
            <div className="hero-cta">
              <Button 
                className="cta-primary"
                onClick={() => navigate('/calculator')}
              >
                Calculate Your ROI <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                variant="outline" 
                className="cta-secondary"
                onClick={() => navigate('/technology')}
              >
                View Technology
              </Button>
            </div>
          </div>
          
          <div className="hero-stats">
            <div className="stat-card">
              <div className="stat-icon">
                <Wind />
              </div>
              <div className="stat-value">40%</div>
              <div className="stat-label">Small Wind Efficiency</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">
                <TrendingUp />
              </div>
              <div className="stat-value">4-6yr</div>
              <div className="stat-label">Payback Period</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">
                <Shield />
              </div>
              <div className="stat-value">100yr</div>
              <div className="stat-label">Structure Lifetime</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">
                <Cpu />
              </div>
              <div className="stat-value">24/7</div>
              <div className="stat-label">Cloud Monitoring</div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Overview Section */}
      <section className="overview-section">
        <div className="container">
          <h2 className="section-title">The Energy Crisis Solution</h2>
          <div className="crisis-grid">
            <div className="crisis-card">
              <div className="crisis-number">759M</div>
              <div className="crisis-text">People without electricity</div>
            </div>
            <div className="crisis-card">
              <div className="crisis-number">Rising</div>
              <div className="crisis-text">Energy costs globally</div>
            </div>
            <div className="crisis-card">
              <div className="crisis-number">Unstable</div>
              <div className="crisis-text">Grid infrastructure</div>
            </div>
            <div className="crisis-card">
              <div className="crisis-number">Diesel</div>
              <div className="crisis-text">Dependence & pollution</div>
            </div>
          </div>
          
          <div className="solution-banner">
            <h3>The Breakthrough: Profitable Small-Scale Distributed Energy</h3>
            <Button 
              size="lg"
              className="cta-primary mt-4"
              onClick={() => navigate('/technology')}
            >
              Discover Our Technology
            </Button>
          </div>
        </div>
      </section>

      {/* Key Features Preview */}
      <section className="features-preview">
        <div className="container">
          <div className="features-split">
            <div className="features-image">
              <img 
                src="https://images.unsplash.com/photo-1639422026989-c17351e8c71e" 
                alt="Wind Turbine Technology"
              />
            </div>
            <div className="features-content">
              <h2 className="section-title">Technology Differentiation</h2>
              <ul className="features-list">
                <li>
                  <span className="feature-bullet"></span>
                  <span>40% small-wind efficiency with single-blade VAWT design</span>
                </li>
                <li>
                  <span className="feature-bullet"></span>
                  <span>Cloud-optimised TSR algorithms for maximum energy capture</span>
                </li>
                <li>
                  <span className="feature-bullet"></span>
                  <span>Encrypted power management with hardware-locked firmware</span>
                </li>
                <li>
                  <span className="feature-bullet"></span>
                  <span>Internal strain monitoring for predictive maintenance</span>
                </li>
                <li>
                  <span className="feature-bullet"></span>
                  <span>Remote shutdown capability and fleet management</span>
                </li>
                <li>
                  <span className="feature-bullet"></span>
                  <span>Hybrid solar integration for 24/7 renewable energy</span>
                </li>
              </ul>
              <p className="features-note">
                No competitor offers this complete technology stack.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <h2 className="cta-title">Ready to Deploy Renewable Infrastructure?</h2>
          <p className="cta-text">
            Calculate your ROI, explore our interactive platform, or connect with our team
          </p>
          <div className="cta-buttons">
            <Button 
              size="lg" 
              className="cta-primary"
              onClick={() => navigate('/calculator')}
            >
              Financial Calculator
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="cta-secondary"
              onClick={() => navigate('/platform')}
            >
              Live Platform Demo
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="cta-secondary"
              onClick={() => navigate('/investors')}
            >
              Investor Information
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
